import { HOME_ACTIONS, HOME_AUDIENCES, WIDGET_HOME_LIMITS } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { Button, Dialog, DialogContent, Field, Input, Segmented, Select } from "@sagot/ui";
import { useState, type SyntheticEvent } from "react";
import { HomeOptionTarget, TARGET_FIELD_ID } from "./home-option-target";
import { IconPicker } from "./icon-picker";
import { optionErrors, type HomeOptionForm } from "./home-model";
import type { HomeChoices } from "./use-home-data";

const K = "deployKnowledge.widgetStudio.home";
const LABEL_ID = "home-option-label";
const FIELD_IDS: Partial<Record<keyof HomeOptionForm, string>> = {
  label: LABEL_ID,
  description: "home-option-description",
  workflow_id: TARGET_FIELD_ID,
  action_id: TARGET_FIELD_ID,
  question: TARGET_FIELD_ID,
  url: TARGET_FIELD_ID,
};

/** Radix would focus the header's close button first; start on the label instead. */
const focusOnMount = (element: HTMLInputElement | null) => {
  element?.focus();
};

interface HomeOptionDialogProps {
  /** The option being edited (a blank one when adding). */
  initial: HomeOptionForm;
  isNew: boolean;
  agentId: string;
  workflows: HomeChoices;
  bookings: HomeChoices;
  onSave: (option: HomeOptionForm) => void;
  onClose: () => void;
}

/** Add / edit one Home option. Validates on Save and focuses the first field that needs attention. */
export function HomeOptionDialog({ initial, isNew, agentId, workflows, bookings, onSave, onClose }: HomeOptionDialogProps) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState(initial);
  const [attempted, setAttempted] = useState(false);
  const errors = attempted ? optionErrors(draft) : {};
  const patch = (change: Partial<HomeOptionForm>) => { setDraft((current) => ({ ...current, ...change })); };

  const submit = (event: SyntheticEvent) => {
    event.preventDefault();
    const found = optionErrors(draft);
    const first = (Object.keys(found) as (keyof HomeOptionForm)[]).find((field) => FIELD_IDS[field]);
    if (!first) {
      onSave({ ...draft, label: draft.label.trim() });
      return;
    }
    setAttempted(true);
    setTimeout(() => { document.getElementById(FIELD_IDS[first] ?? LABEL_ID)?.focus(); }, 0);
  };
  const message = (field: keyof HomeOptionForm) => {
    const code = errors[field];
    return code ? t(`${K}.errors.${code}`) : undefined;
  };

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent title={t(isNew ? `${K}.dialog.addTitle` : `${K}.dialog.editTitle`)} description={t(`${K}.dialog.intro`)} closeLabel={t("common.close")} className="max-w-lg">
        <form noValidate onSubmit={submit} className="space-y-4">
          <IconPicker value={draft.icon} onChange={(icon) => { patch({ icon }); }} />
          <Field id={LABEL_ID} label={t(`${K}.dialog.label`)} error={message("label")}>
            <Input ref={focusOnMount} maxLength={WIDGET_HOME_LIMITS.label} placeholder={t(`${K}.dialog.labelPlaceholder`)} value={draft.label} onChange={(event) => { patch({ label: event.target.value }); }} />
          </Field>
          <Field id={FIELD_IDS.description} label={`${t(`${K}.dialog.descriptionLabel`)} ${t(`${K}.dialog.optional`)}`} error={message("description")}>
            <Input maxLength={WIDGET_HOME_LIMITS.description} placeholder={t(`${K}.dialog.descriptionPlaceholder`)} value={draft.description} onChange={(event) => { patch({ description: event.target.value }); }} />
          </Field>
          <div>
            <span className="label">{t(`${K}.dialog.whenTapped`)}</span>
            <Segmented
              aria-label={t(`${K}.dialog.whenTapped`)}
              value={draft.action}
              onValueChange={(action) => { patch({ action }); }}
              options={HOME_ACTIONS.map((action) => ({ value: action, label: t(`${K}.actions.${action}`) }))}
            />
            <div className="mt-3">
              <HomeOptionTarget option={draft} errors={errors} workflows={workflows} bookings={bookings} agentId={agentId} onChange={patch} />
            </div>
          </div>
          <Field label={t(`${K}.dialog.showTo`)}>
            <Select value={draft.audience} onChange={(event) => { patch({ audience: HOME_AUDIENCES.find((audience) => audience === event.target.value) ?? draft.audience }); }}>
              {HOME_AUDIENCES.map((audience) => (
                <option key={audience} value={audience}>
                  {t(`${K}.audience.${audience}`)}
                </option>
              ))}
            </Select>
          </Field>
          <div className="flex justify-end gap-2 border-t border-line pt-3">
            <Button type="button" variant="secondary" onClick={onClose}>
              {t(`${K}.dialog.cancel`)}
            </Button>
            <Button type="submit">{t(`${K}.dialog.save`)}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
